import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig} from 'axios';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
    exp: number;
    [key: string]: any;
}

class AxiosService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
            timeout: 120000, // Timeout après 10 secondes
        });

        this.axiosInstance.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                const token = sessionStorage.getItem('tokenAdminUI');
                if (token) {
                    if (this.isTokenExpired(token)) {
                        const newToken = this.refreshToken();
                        if (newToken) {
                            // @ts-ignore
                            config.headers['Authorization'] = `Bearer ${newToken}`;
                        }
                    } else {
                        // @ts-ignore
                        config.headers['Authorization'] = `Bearer ${token}`;
                    }
                }

                return config;
            },
            (error) => {
                if (error.response && error.response.status === 401) {
                    sessionStorage.removeItem('tokenAdminUI');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );

        // Ajouter un intercepteur de réponse
        this.axiosInstance.interceptors.response.use(
            this.handleResponse.bind(this),
            (error) => {
                if (error.response && error.response.status === 401) {
                    sessionStorage.removeItem('tokenAdminUI');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    private handleResponse(response: AxiosResponse) {
        return response;
    }

    private isTokenExpired(token: string): boolean {
        if (!token) return true;

        const decodedToken: DecodedToken =  jwtDecode<DecodedToken>(token);
        const currentTime = Date.now() / 1000;

        return decodedToken.exp < currentTime;
    }

    private async refreshToken(): Promise<string | null> {
        const API_AUTH_URL = process.env.REACT_APP_API_AUTH_URL;
//        const token = sessionStorage.getItem('tokenAdminUI');

        try {
            const response = await axios.post(`${API_AUTH_URL}/api/refresh-token`, {
                refreshToken: sessionStorage.getItem('tokenAdminUI'),
            });

            const { accessToken } = response.data.accessToken;
            sessionStorage.setItem('tokenAdminUI', accessToken);
            return accessToken;

        } catch (error) {
            console.error('Erreur lors du rafraîchissement du token', error);
            return null;
        }
    }

    public get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.get<T>(url, config);
    }

    public post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.post<T>(url, data, config);
    }

    public put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.put<T>(url, data, config);
    }

    public delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.delete<T>(url, config);
    }
}

const axiosService = new AxiosService();
export default axiosService;
